import React from "react";
import SEO from "../Components/SEO";
import Card2 from "../Images/Headers/public-health.jpg";
import Card1 from "../Images/CIRCODU-BANNERS-3.jpg";
import "../Pages/OurHistory.css";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import "../Pages/NewsandEvents.css";

function PublicHealth() {
  return (
    <div className="ne-page">
      <SEO
        title="Public Health"
        description="CIRCODU's public health thematic area focuses on improving health outcomes by addressing the root causes of poor health, including poverty, inequality, and environmental risk factors, through community partnerships and evidence-based interventions."
        keywords="public health, health outcomes, poverty, inequality, environmental risk factors, community partnerships, evidence-based interventions, Sustainable Development Goal 3, healthcare access, healthy lifestyle behaviors, environmental determinants of health, health policy advocacy, CIRCODU public health Uganda"
        url="/our-thematic-areas/public-health"
        image={Card1}
        type="website"
      />

      {/* Hero */}
      <div className="ne-hero">
        <div className="ne-hero__image">
          <img src={Card2} alt="Public Health Header" />
        </div>
        <div className="ne-hero__overlay">
          <h4 className="ne-hero__title">Public Health</h4>
          <Breadcrumb>
            <Breadcrumb.Item className="text1" href="/">
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item className="text1">
              Our Thematic Areas
            </Breadcrumb.Item>
            <Breadcrumb.Item className="text1">Public Health</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <section className="section border-bottom">
        <div className="container border-top">
          <div className="row">
            <div className="col-md-12 mt-2">
              {/* Intro */}
              <h5 style={{ color: "#008250", fontSize: "16px" }}>
                <b>Public Health</b>
              </h5>
              <p className="texty">
                Health is not a privilege — it is a foundation. Without it,
                communities cannot learn, work, grow, or thrive. CIRCODU
                recognizes that public health is inseparable from sustainable
                development, and that addressing poor health outcomes requires
                tackling the deeper forces that drive them: poverty, inequality,
                lack of access to services, and environmental degradation.
              </p>
              <p className="texty">
                Through partnerships with communities, governments, and
                development organizations, CIRCODU works to deliver health
                interventions that are practical, evidence-based, and built for
                lasting impact. Our work contributes directly to{" "}
                <b>Sustainable Development Goal 3,</b> which calls for ensuring
                healthy lives and promoting well-being for all at all ages.
              </p>

              {/* Our Overall Objective */}
              <h5 style={{ color: "#008250", fontSize: "14px" }}>
                <b>🎯 Our Overall Objective</b>
              </h5>
              <p className="texty">
                To improve public health outcomes across Uganda by partnering
                with communities and stakeholders to{" "}
                <b>
                  expand access to healthcare services, promote healthy
                  lifestyle behaviors, and advocate for policies that address
                  the root causes of poor health,
                </b>{" "}
                including poverty, inequality, and environmental risk factors.
              </p>

              {/* Specific Objectives */}
              <h5 style={{ color: "#008250", fontSize: "14px" }}>
                <b>🔑 Specific Objectives</b>
              </h5>
              <ol className="texty mt-1">
                <li>
                  <b>Improve Access to Healthcare Services:</b> Work with
                  partners to expand the reach of quality healthcare services to
                  underserved communities, ensuring that{" "}
                  <b>no one is denied access to essential health care</b>{" "}
                  because of where they live or what they earn.
                </li>
                <li className="mt-1">
                  <b>Promote Healthy Lifestyle Behaviors:</b> Empower
                  individuals and communities with the knowledge and tools to
                  make informed decisions about{" "}
                  <b>
                    nutrition, hygiene, physical activity, and disease
                    prevention,
                  </b>{" "}
                  reducing the burden of both communicable and non-communicable
                  diseases.
                </li>
                <li className="mt-1">
                  <b>Address Environmental Determinants of Health:</b> Recognize
                  and respond to the direct links between environmental
                  conditions and health outcomes, including{" "}
                  <b>
                    indoor air pollution, unsafe water, and poor sanitation,
                  </b>{" "}
                  by integrating health considerations into CIRCODU's energy,
                  water, and waste management programs.
                </li>
                <li className="mt-1">
                  <b>Advocate for Health-Supportive Policies:</b> Engage with
                  policymakers and development partners to champion{" "}
                  <b>
                    policies and investments that promote equitable access to
                    health services
                  </b>{" "}
                  and create the enabling conditions for communities to live
                  healthier, longer lives.
                </li>
              </ol>
            </div>
            <div className="col-md-8">
              {/* Our Actions on the Ground */}
              <h5 style={{ color: "#008250", fontSize: "14px" }}>
                <b>⚙️ Our Actions on the Ground</b>
              </h5>
              <ol className="texty mt-1">
                <li>
                  🏥 <b>Community Health Awareness Campaigns:</b> CIRCODU
                  designs and delivers targeted health education programs that
                  equip communities with practical knowledge on{" "}
                  <b>
                    disease prevention, maternal health, nutrition, and hygiene
                  </b>{" "}
                  — meeting people where they are and in the languages they
                  understand.
                </li>
                <li className="mt-1">
                  🌿 <b>Integrating Health into Clean Energy Programs:</b> Many
                  of CIRCODU's clean cooking and energy efficiency interventions
                  are fundamentally public health interventions. By reducing
                  exposure to{" "}
                  <b>
                    harmful indoor air pollutants from wood and charcoal
                    cookstoves,
                  </b>{" "}
                  we directly protect the lungs, hearts, and lives of women and
                  children across Uganda.
                </li>
                <li className="mt-1">
                  💧 <b>Water, Sanitation, and Hygiene (WASH) Integration:</b>{" "}
                  Through our water and sanitation programs, CIRCODU promotes{" "}
                  <b>
                    safe drinking water, improved sanitation facilities, and
                    community hygiene practices
                  </b>{" "}
                  that reduce the prevalence of waterborne diseases and
                  diarrhoeal illnesses, particularly among children.
                </li>
                <li className="mt-1">
                  🤝 <b>Partnerships for Health Impact:</b> CIRCODU actively
                  collaborates with local health authorities, NGOs, and
                  international development partners to{" "}
                  <b>co-design and co-deliver health interventions</b> that are
                  context-specific, community-owned, and aligned with Uganda's
                  national health priorities.
                </li>
                <li className="mt-1">
                  📋 <b>Health Policy Advocacy:</b> We engage with government
                  bodies and policymakers to advocate for{" "}
                  <b>increased investment in community health infrastructure</b>{" "}
                  and the development of policies that reduce health
                  inequalities and ensure that vulnerable populations are not
                  left behind.
                </li>
              </ol>
            </div>
            <div
              className="col-md-4 mb-1"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="imagecard" style={{ width: "100%" }}>
                <img
                  src={Card1}
                  alt="CIRCODU Sustainable Energy Programs"
                  width="100%"
                  style={{ borderRadius: "5%", objectFit: "cover" }}
                />
              </div>
            </div>
            <hr />

            <div className="col-md-12">
              {/* Closing statement */}
              <p className="texty">
                <i>
                  Because healthy communities are the foundation of everything
                  we build. At CIRCODU, is the thread that runs through all of
                  our work.
                </i>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PublicHealth;
